import { SPRITESHEET_UI } from "Data/Constants";
import { GameConfig, ItemID } from "Data/GameConfig";
import { ITEM_FOUND } from "Data/Topics";
import { Button } from "GameObjects/Button";
import { TimelineLite } from "gsap/TimelineLite";
import { getTexture, makeSprite } from "html-living-framework/lib/Utils/PixiUtils";
import type { IItemConfig } from "Interfaces/IItemConfig";
import type { IItemState } from "Interfaces/IItemState";
import type { ILevelState } from "Interfaces/ILevelState";
import { Emitter } from "pixi-particles";

const MAX_ICON_SIZE = 52;

export class ExteriorItemIcon extends Button {

    protected checkmark: PIXI.Sprite;
    protected _subscriptions: any[] = [];
    protected id: ItemID;
    protected found: boolean;
    protected particleBurst?: Emitter;
    protected itemFoundTimeline?: TimelineLite;

    constructor(config: IItemConfig, state: IItemState, levelState: ILevelState) {
        super(
            () => {this.playHoverVO(); },
            levelState.id + "/" + config.icon_ext,
            GameConfig.spritesheets[levelState.id],
            config.vo.returned,
        );
        this.id = state.id;
        this.found = state.found;
        this._image!.scale.set(Math.min(MAX_ICON_SIZE / this._image!.width, MAX_ICON_SIZE / this._image!.height));

        this.checkmark = this.addChild(makeSprite("checkmark", SPRITESHEET_UI));
        this.checkmark.visible = false;

        this.particleBurst = new Emitter(this, [
            getTexture("sparkle-1", SPRITESHEET_UI),
            getTexture("sparkle-2", SPRITESHEET_UI),
            getTexture("sparkle-3", SPRITESHEET_UI),
            getTexture("sparkle-4", SPRITESHEET_UI),
        ], {
            alpha: {
                start: 1,
                end: 0.2,
            },
            scale: {
                start: 0.2,
                end: 0.5,
                minimumScaleMultiplier: 1,
            },
            color: {
                start: "#ffffff",
                end: "#ffffff",
            },
            speed: {
                start: 200,
                end: 50,
                minimumSpeedMultiplier: 1,
            },
            acceleration: {
                x: 0,
                y: 0,
            },
            maxSpeed: 0,
            startRotation: {
                min: 0,
                max: 360,
            },
            noRotation: false,
            rotationSpeed: {
                min: 0,
                max: 0,
            },
            lifetime: {
                min: 0.25,
                max: 0.75,
            },
            blendMode: "normal",
            frequency: 0.001,
            emitterLifetime: 0.1,
            maxParticles: 100,
            pos: {
                x: 0,
                y: 0,
            },
            addAtBack: false,
            spawnType: "circle",
            spawnCircle: {
                x: 0,
                y: 0,
                r: 10,
            },
        });
        this.particleBurst.emit = false;
        this.particleBurst.autoUpdate = true;

 /*
        const g = new PIXI.Graphics();
        this.addChild(g);
        g.beginFill(0x000000, 0.2);
        g.drawRect(-MAX_ICON_SIZE / 2, -MAX_ICON_SIZE / 2, MAX_ICON_SIZE, MAX_ICON_SIZE);
        g.endFill();
 */

        this.SetState(state);

        this._subscriptions.push(
            PubSub.subscribe(ITEM_FOUND, this.onMessageReceived.bind(this)),
        );
    }

    public destroy(pOptions?: PIXI.DestroyOptions | boolean) {
        this.itemFoundTimeline?.kill();
        this.particleBurst?.cleanup();
        this.particleBurst?.destroy();
        this._subscriptions.forEach(PubSub.unsubscribe);
        super.destroy(pOptions);
    }

    protected onMessageReceived(pTopic: string, pData: any) {
        switch (pTopic) {
            case ITEM_FOUND:
                if ((pData as IItemState).id === this.id) {
                    this.SetState(pData);
                }
                break;
        }
    }

    protected PlayItemFoundAnimation() {
        this.itemFoundTimeline?.kill();
        this.itemFoundTimeline = new TimelineLite();
        this.itemFoundTimeline
            // 0.5s -> 1s
            // scale up
            .to(this, 0.5, {pixi: {scale: 1.3}}, 0.5)

            // 1s -> 1.5s
            // play particles
            .call(() => { this.particleBurst?.playOnceAndDestroy(); })
            // AND fade from white to colour
            .call(() => {
                this._visuals.alpha = 0.3;
                this.checkmark.visible = true;
            })
            // AND scale down
            .to(this, 0.5, {pixi: {scale: 1}}, 1)

            // everything you just read, but three seconds later
            .delay(3)
        ;
    }

    private SetState(state: IItemState) {
        if (!state.found) {
            this._visuals.alpha = 1;
        }
        else if (!this.found) {
            this.PlayItemFoundAnimation();
        }
        else {
            this._visuals.alpha = 0.3;
            this.checkmark.visible = true;
        }

        this.found = state.found;
    }
}
